import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Theme, Button, Typography, Box, Tooltip } from '@mui/material';
import { menuLightGrey, tokenAlpha } from 'styles/vars';
import { useSelector, useDispatch } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import {
  app,
  getDifferencesLoadedSelector,
  inspection,
  getBarcodeDifferencesExist,
  getBrailleDifferencesExist,
  getAllPageLoadedSelector,
  getCtfJobs,
  getSelectedModule,
  getShowMarkupTab,
  getDisplayedDifferenceGroups,
  getDifferences,
  getDifferenceViewOptions,
  getInspectionId,
  getInspectionSettings,
  getSourceFileUnavailable,
  getTargetFileUnavailable,
} from 'store';
import { isResultUrl } from 'utils/location';
import FilterList from '../shared/FilterList';
import OrderingList from './OrderingList';
import ListOfDifferences from './ListOfDifferences';
import StepAndRepeatListOfDifferences from './StepAndRepeatListOfDifferences';
import NoBarcodeNotification from './NoBarcodeNotification';
import NoBrailleNotification from './NoBrailleNotification';
import ResultsTabAlerts from './ResultsTabAlerts';
import BulkActionModal from './BulkActionModal';
import { makeStyles } from 'tss-react/mui';
import { GVTypography } from 'components/lib';
import { colors } from 'components/lib/global/styles';
import {
  CTFModuleTypes,
  Difference,
  PDFTronTools,
  SortDifferencesOption,
  PDFTRON_DEFAULT_TOOL,
  DocumentTypes,
} from 'types';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import CheckCircleIcon from 'components/icons/CustomStatuses/CicleCheckIcon';
import WarningIcon from 'components/icons/CustomStatuses/WarningIcon';
import { useOrganizationQuery } from 'store/queries/organizations/useOrganizationQuery';
import { getOrganizationId } from 'utils/auth';
import { useUserStateStore } from 'zstore/userStateStore';
import { updateDifferenceViewOptions, updateInspection } from 'store/request/inspections/actions';
import { getHideDisplayPanelsForModule } from 'store/app/appSelectors';
import BulkActionToggle from './BulkActionToggle';
import { bulkUpdateDifferences, BulkUpdateData } from 'store/request/differences/actions';
import BulkEdit from 'components/icons/BulkActionsIcons/BulkEdit';
import MultipleSelectionCheckbox from './MultipleSelectionCheckbox';
import {
  getIsStepAndRepeat,
  getRepeatsPerPage,
  getRepeats,
  getSelectedTool,
  getLastJobMetadata,
  getLastJobCtfVersion,
} from 'store/inspection/inspectionSelectors';
import StepAndRepeatDropdown from './StepAndRepeatDropdown';
import { useTracker } from '../../Tracker/TrackerProvider';
import MarkupToggleButton from './MarkupToggleButton';
import ResyncToggle from './ResyncToggle';
import selectTool from 'utils/selectTool/selectTool';
import { PDFManagerFactory } from 'pdftron';
import MergeGraphicsButton from './MergeGraphicsButton';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { isInternal } from 'components/Guards/InternalGuard';
import { useQuery } from '@redux-requests/react';
import { fetchInspection } from 'store/request/inspections/actions';
import { InspectionStatuses } from 'types';
import { useFlags } from 'launchdarkly-react-client-sdk';

/** Max width of the module tab strip; scroll layer uses maxWidth so content wider than this scrolls */
const MODULE_STRIP_MAX_WIDTH_PX = 330;
/** Height of the horizontal scrollbar gutter in the module tab strip (webkit) */
const SCROLLBAR_HEIGHT_PX = 6;

const useStyles = makeStyles()((theme: Theme) => {
  return {
    root: {
      height: '100%',
    },
    options: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(2),
    },
    buttonsContainer: {
      marginLeft: 0,
      marginRight: theme.spacing(2),
      '& .MuiButton-root.Mui-disabled, & .MuiToggleButton-root.Mui-disabled': {
        border: 'none',
      },
    },
    listOfDifferences: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      marginBottom: '8px',
    },
    modules: {
      backgroundColor: colors.darkText,
      borderRadius: '6px',
      marginLeft: '5px',
      minWidth: 0,
      maxWidth: `${MODULE_STRIP_MAX_WIDTH_PX}px`,
      width: 'fit-content',
      position: 'relative',
      overflow: 'hidden',
    },
    modulesHasScrollbar: {
      transition: 'margin-bottom 0.2s ease',
      '&:hover, &:focus-within': {
        marginBottom: -SCROLLBAR_HEIGHT_PX,
        '& > :first-of-type': {
          marginBottom: 0,
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(100,102,105,0.6)',
          },
        },
      },
      '&:hover > :first-of-type::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#4e4e4e',
      },
    },
    modulesScroll: {
      maxWidth: `${MODULE_STRIP_MAX_WIDTH_PX}px`,
      width: 'max-content',
      minWidth: 0,
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
      transition: 'margin-bottom 0.2s ease',
      '&::-webkit-scrollbar': {
        height: 0,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        borderRadius: SCROLLBAR_HEIGHT_PX,
        transition: 'background-color 0.2s ease',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '@supports not selector(::-webkit-scrollbar)': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&:hover, &:focus-within': {
          scrollbarColor: 'rgba(100,102,105,0.6) transparent',
        },
      },
    },
    modulesScrollOverflow: {
      marginBottom: -SCROLLBAR_HEIGHT_PX,
      '&::-webkit-scrollbar': {
        height: SCROLLBAR_HEIGHT_PX,
      },
    },
    modulesTrack: {
      display: 'flex',
      flexWrap: 'nowrap',
    },
    modulesFade: {
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      width: '48px',
      borderRadius: '0 6px 6px 0',
      pointerEvents: 'none',
      background: `linear-gradient(90deg, ${tokenAlpha(colors.darkText, 0)} 0%, ${colors.darkText} 100%)`,
    },
    inspectionModule: {
      padding: theme.spacing(1, 0.625),
      borderRadius: '5px',
      fontSize: '11px',
      fontWeight: 600,
      margin: theme.spacing(0.25),
      color: colors.gvWhite,
      minWidth: 0,
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    inspectionModuleEnabled: {
      backgroundColor: menuLightGrey,
    },
    resultsHeader: {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontFamily: '"Open Sans"',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '16px',
      display: 'inline-block',
      whiteSpace: 'nowrap',
      marginLeft: '0px',
    },
    reviewAllBox: {
      display: 'flex',
      padding: '2px 5px 3px 5px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '4px',
      borderRadius: '4px',
      background: 'rgba(99, 99, 99, 0.21)',
      marginLeft: '8px',
    },
    reviewAllTypography: {
      color: '#A3A3A3',
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontFamily: '"Open Sans", sans-serif',
      fontSize: '11px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '16px',
    },
    toggleButton: {
      borderRadius: '50%',
      width: '24px',
      height: '24px',
      padding: 0,
      margin: theme.spacing(0.25),
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
        backgroundColor: 'rgba(255,255,255,.04)',
      },
      '&.Mui-selected': {
        color: 'rgba(255,255,255)',
        backgroundColor: 'rgba(255,255,255,.12)',
      },
      '&.Mui-selected:hover': {
        backgroundColor: 'rgba(255,255,255,.12)',
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,.37) !important',
      },
    },
    editSelectedButton: {
      width: '75px',
      height: '16px',
      fontFamily: '"Open Sans"',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '16px',
      letterSpacing: '0px',
      color: '#FFFFFF',
      cursor: 'pointer',
      marginRight: theme.spacing(1),
      '&.disabled': {
        color: '#FFFFFF61',
        cursor: 'default',
      },
    },
    icon: {
      fontSize: '16px',
    },
    disabled: {
      color: 'rgba(255,255,255,.37) !important',
    },
    resultsCountContainer: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      transition: 'padding-left 300ms ease-out',
    },
    ctfVersionText: {
      fontWeight: 400,
      opacity: 0.5,
      fontSize: '9px',
      marginLeft: '1px',
      marginTop: '-14px',
    },
    selectAllCheckboxContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    selectAllContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      position: 'absolute',
      left: '3px',
      top: '50%',
      transform: 'translateY(-50%)',
      cursor: 'pointer',
      zIndex: 5,
    },
    bulkEditButton: {
      borderRadius: '20px',
      background: 'var(--color-secondary-200, #6EDBE2)',
      display: 'flex',
      width: '24px',
      height: '24px',
      padding: '8px',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 0,
      boxShadow: 'none',
      border: 'none',
      marginRight: '8px',
      '&:hover': {
        background: 'var(--color-secondary-200, #6EDBE2)',
        boxShadow: 'none',
      },
    },
    totalRepeatsText: {
      marginBottom: theme.spacing(1),
      color: 'var(--lightgrey-100, #8792AB)',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '100%',
    },
  };
});

const ResultTabView = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tracker = useTracker();
  const { showAllResultsTab } = useFlags();

  const [showBarcodePopup, setShowBarcodePopup] = useState(false);
  const [showBraillePopup, setShowBraillePopup] = useState(false);
  const [multipleSelectionMode, setMultipleSelectionMode] = useState(false);
  const [selectedDifferences, setSelectedDifferences] = useState<Difference[]>([]);
  const selectedDifferenceIds = useMemo(() => selectedDifferences.map((diff) => diff.id), [selectedDifferences]);
  const [allSelected, setAllSelected] = useState(false);
  const [openBulkActionModal, setOpenBulkActionModal] = useState(false);
  const [bulkOverrides, setBulkOverrides] = useState({ overrideStatus: false, overrideComment: false });
  const [isBulkApplying, setIsBulkApplying] = useState(false);
  const [previousSortOption, setPreviousSortOption] = useState<SortDifferencesOption>(
    SortDifferencesOption.readingOrder,
  );
  const moduleScrollRef = useRef<HTMLDivElement | null>(null);
  const [showModuleFade, setShowModuleFade] = useState(false);
  const [modulesOverflow, setModulesOverflow] = useState(false);

  const documentsLoaded = useSelector(getAllPageLoadedSelector);
  const differencesCount = useSelector(getDisplayedDifferenceGroups).length;
  const differencesLoaded = useSelector(getDifferencesLoadedSelector);
  const sourceFileUnavailable = useSelector(getSourceFileUnavailable);
  const targetFileUnavailable = useSelector(getTargetFileUnavailable);
  const { singleFile } = useSelector(getInspectionSettings);
  const areFilesUnavailable = (!singleFile && sourceFileUnavailable) || targetFileUnavailable;
  const barcodeDifferences = useSelector(getBarcodeDifferencesExist);
  const brailleDifferences = useSelector(getBrailleDifferencesExist);
  const rawCtfJobs = useSelector(getCtfJobs);
  const lastJobMetadata = useSelector(getLastJobMetadata);
  const selectedModule = useSelector(getSelectedModule);
  const differences = useSelector(getDifferences);
  const allDiffsViewed = differences.every((diff) => diff.viewed);
  const { user } = useUserStateStore();
  const orgId = getOrganizationId(user);
  const { data: orgData } = useOrganizationQuery(orgId, {
    includeUsers: false,
  });
  const differencesState = useSelector(getDifferenceViewOptions);
  const showMarkupTab = useSelector(getShowMarkupTab);
  const inspectionId = useSelector(getInspectionId);
  const { isShared, isLoading: isCollaboratorsLoading } = useCollaborators(inspectionId);
  const { refetch: refetchStatuses } = useInspectionCustomStatuses(inspectionId);
  const displayedDifferenceGroups = useSelector(getDisplayedDifferenceGroups);
  const repeatsPerPage = useSelector(getRepeatsPerPage);
  const repeats = useSelector(getRepeats);
  const isStepAndRepeat = useSelector(getIsStepAndRepeat);
  const selectedTool = useSelector(getSelectedTool);
  const lastJobCtfVersion = useSelector(getLastJobCtfVersion);

  // When files are unavailable, reset state and redirect to prep tab.
  useEffect(() => {
    if (areFilesUnavailable && !isShared && !isCollaboratorsLoading) {
      if (targetFileUnavailable) {
        dispatch(inspection.actions.resetDocumentState({ documentType: DocumentTypes.target }));
      }
      if (!singleFile && sourceFileUnavailable) {
        dispatch(inspection.actions.resetDocumentState({ documentType: DocumentTypes.source }));
      }
      dispatch(updateInspection(inspectionId, { lastJobId: null, status: InspectionStatuses.idle }));
      navigate(`/inspection/${inspectionId}`);
    }
  }, [
    areFilesUnavailable,
    isShared,
    isCollaboratorsLoading,
    singleFile,
    sourceFileUnavailable,
    inspectionId,
    navigate,
    dispatch,
    targetFileUnavailable,
  ]);

  // Turn off resync tool when changing away from Text tab and delete any zones
  useEffect(() => {
    if (selectedModule !== CTFModuleTypes.text && selectedTool === PDFTronTools.RESYNC) {
      selectTool(PDFTRON_DEFAULT_TOOL);
      const pdfManager = PDFManagerFactory.getPDFDocManager();
      if (pdfManager) {
        pdfManager.deleteResyncZones();
      }
    }
  }, [selectedModule]);

  // Determine if we should use step-and-repeat view
  const useStepAndRepeatView = isStepAndRepeat && selectedModule === CTFModuleTypes.graphics;
  // Keep selectedDifferences in sync with actual differences data
  useEffect(() => {
    if (multipleSelectionMode) {
      setSelectedDifferences((prev) => {
        if (prev.length > 0) {
          return prev.map((selectedDiff) => {
            const currentDiff = differences.find((diff) => diff.id === selectedDiff.id);
            return currentDiff || selectedDiff;
          });
        }
        return prev;
      });
    }
  }, [differences, multipleSelectionMode]);

  const inspectionQuery = useQuery({ type: fetchInspection });
  const lastJob = inspectionQuery.data?.lastJob;

  const ctfJobs = useMemo(() => {
    let jobs = rawCtfJobs;

    // Filter out graphics job if embedded images
    if (lastJobMetadata?.isEmbeddedImages) {
      jobs = jobs.filter((job) => job !== CTFModuleTypes.graphics);
    }

    // Filter out failed jobs to prevent showing 0 results
    if (lastJob?.ctfJobs) {
      const failedJobTypes = lastJob.ctfJobs
        .filter((job) => job.status === InspectionStatuses.error)
        .map((job) => job.type);
      jobs = jobs.filter((job) => !failedJobTypes.includes(job as CTFModuleTypes));
    }

    return jobs;
  }, [rawCtfJobs, lastJobMetadata, lastJob]);

  // Markups live in their own dynamically-created tab, shown only once showMarkupTab is set in the
  // app store (set imperatively when the first markup is created or loaded from the server).
  const moduleTabs = useMemo(
    () => (showMarkupTab ? [...ctfJobs, CTFModuleTypes.markup] : ctfJobs),
    [ctfJobs, showMarkupTab],
  );

  const updateModuleStripLayout = useCallback(() => {
    const scrollElement = moduleScrollRef.current;
    if (!scrollElement) {
      setShowModuleFade(false);
      setModulesOverflow(false);
      return;
    }

    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
    const overflows = maxScrollLeft > 1;
    setModulesOverflow(overflows);
    setShowModuleFade(overflows && scrollElement.scrollLeft < maxScrollLeft - 1);
  }, []);

  useLayoutEffect(() => {
    const scrollElement = moduleScrollRef.current;
    if (!scrollElement) return;

    updateModuleStripLayout();
    scrollElement.addEventListener('scroll', updateModuleStripLayout, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            updateModuleStripLayout();
          })
        : null;

    resizeObserver?.observe(scrollElement);
    if (scrollElement.firstElementChild instanceof HTMLElement) {
      resizeObserver?.observe(scrollElement.firstElementChild);
    }

    return () => {
      scrollElement.removeEventListener('scroll', updateModuleStripLayout);
      resizeObserver?.disconnect();
    };
  }, [ctfJobs, differencesLoaded, documentsLoaded, updateModuleStripLayout]);

  useEffect(() => {
    const scrollElement = moduleScrollRef.current;
    if (!scrollElement || !selectedModule) return;

    const selectedButton = scrollElement.querySelector<HTMLElement>(`[data-module-tab="${selectedModule}"]`);
    selectedButton?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    updateModuleStripLayout();
  }, [selectedModule, ctfJobs, updateModuleStripLayout]);

  // Initialize sort option based on selected module when inspection loads
  useEffect(() => {
    if (differencesLoaded) {
      // For the case where results are fontMetrics only
      if (
        ctfJobs.length === 1 &&
        ctfJobs[0] === CTFModuleTypes.fontMetrics &&
        differencesState.sortBy !== SortDifferencesOption.fontSize
      ) {
        dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: SortDifferencesOption.fontSize }));
        // Never dispatch updateDifferenceViewOptions with fontSize, this is a front-end feature only.
      }
    }
  }, [differencesLoaded]);

  useEffect(() => {
    if (
      differencesLoaded &&
      selectedModule !== CTFModuleTypes.fontMetrics &&
      differencesState.sortBy === SortDifferencesOption.fontSize
    ) {
      // If we're on a non-FontMetrics tab but have fontSize sort selected, reset to readingOrder
      const defaultSortOption = SortDifferencesOption.readingOrder;
      dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: defaultSortOption }));
      dispatch(updateDifferenceViewOptions({ sortOptions: { sortBy: defaultSortOption } }, inspectionId));
    }
  }, [differencesLoaded, selectedModule, differencesState.sortBy, inspectionId]);

  const singleTypeSortModules = [
    CTFModuleTypes.graphics,
    CTFModuleTypes.barcode,
    CTFModuleTypes.braille,
    CTFModuleTypes.color,
    CTFModuleTypes.markup,
  ];

  const handleModuleSortChange = (newModule: string | null, currentSortOption: SortDifferencesOption | undefined) => {
    if (newModule === CTFModuleTypes.fontMetrics) {
      // Switching TO the FontMetrics module
      // Save the current sort option if it's not already fontSize
      if (currentSortOption !== SortDifferencesOption.fontSize) {
        const savedOption = currentSortOption || SortDifferencesOption.readingOrder;
        const newSortValue = SortDifferencesOption.fontSize;

        dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: newSortValue }));
        // Never dispatch updateDifferenceViewOptions with fontSize, this is a front-end feature only.

        // Update the previous sort option for next time
        setPreviousSortOption(savedOption);
      }
    } else if (selectedModule === CTFModuleTypes.fontMetrics) {
      // Switching AWAY from the FontMetrics module
      // Only restore the previous sort option if the current sort is still fontSize
      if (currentSortOption === SortDifferencesOption.fontSize) {
        const isSingleTypeTarget = singleTypeSortModules.includes(newModule as CTFModuleTypes);
        // If going to a single-type module, differenceType is not available there — clip to readingOrder
        const sortToRestore =
          isSingleTypeTarget && previousSortOption === SortDifferencesOption.differenceType
            ? SortDifferencesOption.readingOrder
            : previousSortOption;
        dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: sortToRestore }));
        // Never send fontSize to the server
        const sortForServer =
          sortToRestore === SortDifferencesOption.fontSize ? SortDifferencesOption.readingOrder : sortToRestore;
        dispatch(updateDifferenceViewOptions({ sortOptions: { sortBy: sortForServer } }, inspectionId));
      }
    } else if (singleTypeSortModules.includes(newModule as CTFModuleTypes)) {
      // Switching TO a single-type module (Graphics, Barcode, Braille, Color)
      // Difference Type sort is not available — reset to Reading Order
      if (currentSortOption === SortDifferencesOption.differenceType) {
        dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: SortDifferencesOption.readingOrder }));
        dispatch(
          updateDifferenceViewOptions({ sortOptions: { sortBy: SortDifferencesOption.readingOrder } }, inspectionId),
        );
      }
    }
  };

  const setSelectedModuleViewOptions = useCallback(
    (module: CTFModuleTypes | 'All') => {
      dispatch(app.actions.setSelectedModule(module));
      dispatch(app.actions.setHideDisplayPanels(getHideDisplayPanelsForModule(module)));
    },
    [dispatch],
  );

  const handleSetMultipleSelectionMode = (mode: boolean) => {
    setMultipleSelectionMode(mode);
  };

  const toggleNoBarcodeDisplay = () => {
    setShowBarcodePopup(false);
  };

  const toggleNoBrailleDisplay = () => {
    setShowBraillePopup(false);
  };

  /** In this useEffect:
   *  Wrapping this grouping pop-up around a grouping existing condition
   *  Counting the number of differences in the group list
   * */

  useEffect(() => {
    // Check if barcode was actually executed in the last job (not just if it's enabled in current settings)
    // Only show popup if the job completed successfully but found no results
    const barcodeJob = lastJob?.ctfJobs?.find((job) => job.type === CTFModuleTypes.barcode);
    const barcodeJobCompleted = barcodeJob?.status === InspectionStatuses.completed;

    if (lastJobMetadata?.isBarcode && !barcodeDifferences && differencesLoaded && barcodeJobCompleted) {
      setShowBarcodePopup(true);
    } else {
      setShowBarcodePopup(false);
    }
  }, [lastJobMetadata?.isBarcode, barcodeDifferences, differencesLoaded, lastJob]);

  useEffect(() => {
    // Check if braille was actually executed in the last job (not just if it's enabled in current settings)
    // Only show popup if the job completed successfully but found no results
    const brailleJob = lastJob?.ctfJobs?.find((job) => job.type === CTFModuleTypes.braille);
    const brailleJobCompleted = brailleJob?.status === InspectionStatuses.completed;

    if (lastJobMetadata?.isBraille && !brailleDifferences && differencesLoaded && brailleJobCompleted) {
      setShowBraillePopup(true);
    } else {
      setShowBraillePopup(false);
    }
  }, [lastJobMetadata?.isBraille, brailleDifferences, differencesLoaded, lastJob]);

  const displayPopups = () => {
    /*
     *  since we only have 2 (soon to be 3) popups I have just done if else.
     *  When we get to a point that there will be different types of popups we can implement numbered priority.
     */
    let popup;
    // const tableEhancement = notificationPopUps.find(noti => noti.type === DialogPopupTypes.TableEnhancementPopup); VE-1393
    // if (tableEhancement && !tableEhancement.dismissed) {
    //   popup = <TableEnhancementsNotification open={showTableEnhancementPopup} />;
    // } else VE-1393

    if (showBarcodePopup && isResultUrl(location)) {
      popup = <NoBarcodeNotification open={showBarcodePopup} onClose={toggleNoBarcodeDisplay} />;
    }

    if (showBraillePopup && isResultUrl(location)) {
      popup = <NoBrailleNotification open={showBraillePopup} onClose={toggleNoBrailleDisplay} />;
    }

    return popup;
  };

  // Canonical display order for module tabs (no 'All' — added at render time when flag is on).
  const desiredModuleOrder = [
    CTFModuleTypes.text,
    CTFModuleTypes.spelling,
    CTFModuleTypes.graphics,
    CTFModuleTypes.barcode,
    CTFModuleTypes.braille,
    CTFModuleTypes.fontMetrics,
    CTFModuleTypes.color,
    CTFModuleTypes.markup,
  ];

  // Auto-selection is programmatic and must only move the selected module — it must NOT change panel
  // visibility. Hiding the source panel here (as an earlier version did, via setSelectedModuleViewOptions
  // + a selectedModule->panels sync effect) can fire while the source document is still loading; the
  // autosave gate (getAllPageLoadedSelector) then passes on target-only and persists an input that
  // doesn't reflect the source, which the backend reads as a real change and flips the inspection to
  // PENDING — spuriously surfacing the "outdated inspection" banner. Panel visibility is only changed on
  // explicit user tab clicks (see onClick -> setSelectedModuleViewOptions).
  useEffect(() => {
    if (moduleTabs.length === 1) {
      dispatch(app.actions.setSelectedModule(moduleTabs[0]));
    } else if (moduleTabs.length > 1) {
      const selectionIsValid =
        selectedModule === 'All' ? showAllResultsTab : moduleTabs.includes(selectedModule as CTFModuleTypes);

      if (!selectionIsValid) {
        // Selected tab is unavailable (All hidden, or a stale Markups selection after the last markup was removed) —
        // auto-select the first tab in display order (matches the leftmost tab).
        const firstTab = [...moduleTabs].sort(
          (a, b) => desiredModuleOrder.indexOf(a as CTFModuleTypes) - desiredModuleOrder.indexOf(b as CTFModuleTypes),
        )[0];
        handleModuleSortChange(firstTab, differencesState.sortBy);
        dispatch(app.actions.setSelectedModule(firstTab));
      } else if (selectedModule) {
        // If current selected module is a failed job, switch to first successful job
        const selectedJob = lastJob?.ctfJobs?.find((job) => job.type === selectedModule);
        if (selectedJob?.status === InspectionStatuses.error) {
          const firstSuccessfulJob = ctfJobs.find((jobType) => {
            const job = lastJob?.ctfJobs?.find((j) => j.type === jobType);
            return job?.status === InspectionStatuses.completed;
          });
          if (firstSuccessfulJob) {
            dispatch(app.actions.setSelectedModule(firstSuccessfulJob));
          }
        }
      }
    }
  }, [moduleTabs, ctfJobs, lastJob, selectedModule, showAllResultsTab, differencesState.sortBy, dispatch]);

  const moduleTabLabel: Record<string, string> = {
    [CTFModuleTypes.fontMetrics]: 'Checks',
    [CTFModuleTypes.markup]: 'Markups',
  };

  const moduleSingleLabel: Record<string, string> = {
    [CTFModuleTypes.fontMetrics]: 'Font Size Check',
  };

  const moduleVisibility = () => {
    if (moduleTabs.length === 1) {
      const label = moduleSingleLabel[moduleTabs[0]] ?? moduleTabs[0];
      return <GVTypography fontWeight={600} fontSize="18px" lineHeight="26px">{`${label} Results`}</GVTypography>;
    } else if (moduleTabs.length > 1) {
      const moduleOrderWithAll = ['All', ...desiredModuleOrder];
      return (
        <Box className={`${classes.modules} ${modulesOverflow ? classes.modulesHasScrollbar : ''}`}>
          <Box
            ref={moduleScrollRef}
            className={`${classes.modulesScroll} ${modulesOverflow ? classes.modulesScrollOverflow : ''}`}
          >
            <Box className={classes.modulesTrack}>
              {((showAllResultsTab ? ['All', ...moduleTabs] : [...moduleTabs]) as CTFModuleTypes[])
                .sort((a, b) => {
                  const indexA = moduleOrderWithAll.indexOf(a);
                  const indexB = moduleOrderWithAll.indexOf(b);
                  return indexA - indexB;
                })
                .map((name) => {
                  const onClick = () => {
                    handleModuleSortChange(name, differencesState.sortBy);
                    setSelectedModuleViewOptions(name);
                  };
                  return (
                    <Button
                      key={name}
                      data-module-tab={name}
                      className={`${classes.inspectionModule} ${
                        selectedModule === name ? classes.inspectionModuleEnabled : ''
                      }`}
                      disableRipple
                      onClick={onClick}
                    >
                      {moduleTabLabel[name] ?? name}
                    </Button>
                  );
                })}
            </Box>
          </Box>
          {showModuleFade && <Box className={classes.modulesFade} />}
        </Box>
      );
    }
    return null;
  };

  useEffect(() => {
    if (differencesCount === 0 && multipleSelectionMode) {
      setMultipleSelectionMode(false);
    }
  }, [differencesCount, multipleSelectionMode]);

  // Reset selected differences when turning off multiple selection mode
  useEffect(() => {
    if (!multipleSelectionMode) {
      setSelectedDifferences([]);
      setAllSelected(false);
    }
  }, [multipleSelectionMode]);

  // Synchronize allSelected state with actual selection status
  useEffect(() => {
    if (multipleSelectionMode && displayedDifferenceGroups.length > 0) {
      const visibleSelectedCount = selectedDifferences.filter((diff) =>
        displayedDifferenceGroups.some((displayedDiff) => displayedDiff.id === diff.id),
      ).length;

      setAllSelected(visibleSelectedCount === displayedDifferenceGroups.length);
    } else {
      // Reset allSelected when not in multiple selection mode or no groups to display
      setAllSelected(false);
    }
  }, [displayedDifferenceGroups, selectedDifferences, multipleSelectionMode]);

  // Handle individual difference selection
  const handleDifferenceSelection = useCallback((difference: Difference, checked: boolean) => {
    setSelectedDifferences((prev) => {
      return checked ? [...prev, difference] : prev.filter((diff) => diff.id !== difference.id);
    });
  }, []);

  // Handle select all click
  const handleSelectAllClick = () => {
    if (differencesCount === 0) return;

    if (allSelected) {
      setSelectedDifferences([]);
      setAllSelected(false);
    } else {
      setSelectedDifferences(displayedDifferenceGroups);
      setAllSelected(true);
    }
  };

  const handleOpenBulkActionModal = () => {
    // Track Bulk Edit button click
    tracker.track({
      name: 'bulk-action-bulk-edit-clicked',
      data: {
        selectedCount: selectedDifferences.length,
      },
    });

    setOpenBulkActionModal(true);
  };

  const handleCloseBulkActionModal = () => {
    setOpenBulkActionModal(false);
    setBulkOverrides({ overrideStatus: false, overrideComment: false });
  };

  const handleCancelOverrides = () => {
    setBulkOverrides({ overrideStatus: false, overrideComment: false });
  };

  const trackBulkActions = (
    params: {
      statusId: string;
      comment: string;
      isFalsePositive: boolean;
      selectedStatusTitle?: string;
    },
    modifiedDifferences: {
      statusModifiedCount: number;
      commentModifiedCount: number;
    },
  ) => {
    const baseTrackingData = {
      selectedCount: selectedDifferences.length,
      statusModifiedCount: modifiedDifferences.statusModifiedCount,
      commentModifiedCount: modifiedDifferences.commentModifiedCount,
    };

    if (params.statusId) {
      if (params.statusId === 'unassigned') {
        tracker.track({
          name: 'bulk-action-mark-unassigned-clicked',
          data: baseTrackingData,
        });
      } else {
        tracker.track({
          name: 'bulk-action-status-selected',
          data: {
            ...baseTrackingData,
            statusId: params.statusId,
            statusTitle: params.selectedStatusTitle || 'unknown',
          },
        });
      }
    }

    if (params.comment && params.comment.trim() !== '') {
      tracker.track({
        name: 'bulk-action-comment-added',
        data: {
          ...baseTrackingData,
          commentLength: params.comment.trim().length,
        },
      });
    }

    if (params.isFalsePositive) {
      tracker.track({
        name: 'bulk-action-false-positive-reported',
        data: baseTrackingData,
      });
    }
  };

  const handleApplyBulkAction = (params: {
    statusId: string;
    comment: string;
    isFalsePositive: boolean;
    selectedStatusTitle?: string;
  }) => {
    const statusUpdated = Boolean(params.statusId);
    const commentUpdated = Boolean(params.comment);

    const existingStatus = selectedDifferences.some((diff) => Boolean(diff.statusId));
    const existingComment = selectedDifferences.some((diff) => Boolean(diff.comment));

    const showOverrideStatus = existingStatus && statusUpdated && params.statusId !== 'unassigned';
    const showOverrideComment = existingComment && commentUpdated;

    if (showOverrideStatus || showOverrideComment) {
      setBulkOverrides({
        overrideStatus: showOverrideStatus,
        overrideComment: showOverrideComment,
      });
    } else {
      handleBulkUpdate(params);
    }
  };

  const handleBulkUpdate = (
    params: { statusId: string; comment: string; isFalsePositive: boolean; selectedStatusTitle?: string },
    overrides?: { overrideStatus: boolean; overrideComment: boolean },
  ) => {
    const { overrideStatus, overrideComment } = overrides || { overrideStatus: false, overrideComment: false };
    const statusUpdated = Boolean(params.statusId);
    const commentUpdated = Boolean(params.comment);

    const bulkUpdateData: BulkUpdateData = {
      statusUpdated: {
        differenceIds: [],
        groupIds: [],
        statusId: params.statusId === 'unassigned' ? null : params.statusId || '',
      },
      commentUpdated: {
        differenceIds: [],
        groupIds: [],
        comment: params.comment || '',
      },
    };

    let statusModifiedCount = 0;
    let commentModifiedCount = 0;

    selectedDifferences.forEach((diff) => {
      if (
        statusUpdated &&
        (overrideStatus || !diff.statusId || (!overrideStatus && params.statusId === 'unassigned'))
      ) {
        if (diff.groupId) {
          bulkUpdateData.statusUpdated?.groupIds.push(diff.groupId);
        } else {
          bulkUpdateData.statusUpdated?.differenceIds.push(diff.id);
        }
        statusModifiedCount++;
      }

      if (commentUpdated && (overrideComment || !diff.comment)) {
        if (diff.groupId) {
          bulkUpdateData.commentUpdated?.groupIds.push(diff.groupId);
        } else {
          bulkUpdateData.commentUpdated?.differenceIds.push(diff.id);
        }
        commentModifiedCount++;
      }
    });

    const bulkAction = bulkUpdateDifferences(bulkUpdateData);

    if (bulkAction.meta) {
      const originalOnSuccess = bulkAction.meta.onSuccess;
      const originalOnError = bulkAction.meta.onError;
      bulkAction.meta = {
        ...bulkAction.meta,
        onSuccess: (response, action, store) => {
          if (originalOnSuccess) {
            originalOnSuccess(response, action, store);
          }

          const totalUpdated = (response.data?.differences?.length || 0) + (response.data?.groups?.length || 0);
          if (totalUpdated > 0) {
            trackBulkActions(params, {
              statusModifiedCount,
              commentModifiedCount,
            });
            // Refresh inspection statuses so the status filter menu includes any newly used status
            refetchStatuses();
          }

          setIsBulkApplying(false);
          setMultipleSelectionMode(false);
          setSelectedDifferences([]);
          handleCloseBulkActionModal();
        },
        onError: (error, action, store) => {
          if (originalOnError) {
            originalOnError(error, action, store);
          }
          setIsBulkApplying(false);
        },
      };
    }

    setIsBulkApplying(true);
    dispatch(bulkAction);
  };

  return (
    <Grid item container direction="column" wrap="nowrap" className={classes.root} id="resultTab">
      {areFilesUnavailable && isShared ? (
        <Grid item sx={{ margin: '0 auto', padding: '24px' }} className={classes.options}>
          <GVTypography variant="subtitle1" emphasis="disabled">
            Results not available. The files for this inspection have been removed due to your organization's data
            retention policy. Files cannot be re-uploaded to shared inspections.
          </GVTypography>
        </Grid>
      ) : !differencesLoaded || !documentsLoaded ? (
        <Grid item sx={{ margin: '0 auto' }} className={classes.options}>
          <CircularProgress color="secondary" />
        </Grid>
      ) : (
        <>
          {displayPopups()}
          <ResultsTabAlerts />
          {
            <>
              <Grid item container direction="column" className={classes.options}>
                <Grid item>{moduleVisibility()}</Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    height: '38px',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    marginTop: '8px',
                    marginBottom: '8px',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    {multipleSelectionMode && (
                      <Tooltip title="Select all" placement="bottom">
                        <div className={classes.selectAllContainer} onClick={handleSelectAllClick}>
                          <MultipleSelectionCheckbox
                            checked={allSelected}
                            indeterminate={!allSelected && selectedDifferences.length > 0}
                            className={classes.selectAllCheckboxContainer}
                            aria-label="Select all differences"
                            disabled={differencesCount === 0}
                            variant="selectAll"
                          />
                        </div>
                      </Tooltip>
                    )}

                    <div
                      className={classes.resultsCountContainer}
                      style={{ paddingLeft: multipleSelectionMode ? '30px' : '0' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h6" className={classes.resultsHeader} data-testid="results-header">
                          {differencesCount} {differencesCount === 1 ? 'Result' : 'Results'}
                        </Typography>
                        {orgData?.forceReview && (
                          <Box className={classes.reviewAllBox} data-testid="review-all-required">
                            {allDiffsViewed ? (
                              <CheckCircleIcon />
                            ) : (
                              <Tooltip
                                title="All results must be reviewed before downloading the report"
                                placement="bottom"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      maxWidth: '170px',
                                      borderRadius: '4px',
                                      padding: '6px 8px',
                                      lineHeight: '100%',
                                      fontSize: '11px',
                                      gap: '8px',
                                    },
                                  },
                                }}
                              >
                                <span style={{ display: 'inline-flex' }}>
                                  <WarningIcon />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </span>
                    </div>
                  </div>
                  <Grid
                    item
                    className={classes.buttonsContainer}
                    flexDirection="row"
                    display="flex"
                    alignItems="center"
                  >
                    {multipleSelectionMode && selectedDifferences.length > 0 && (
                      <Tooltip title="Bulk Review" placement="bottom">
                        <Button
                          onClick={handleOpenBulkActionModal}
                          className={classes.bulkEditButton}
                          disableRipple
                          aria-label="Bulk Review"
                          data-testid="BulkEditButton"
                        >
                          <BulkEdit />
                        </Button>
                      </Tooltip>
                    )}
                    <BulkActionToggle
                      handleSetMultipleSelectionMode={handleSetMultipleSelectionMode}
                      multipleSelectionMode={multipleSelectionMode}
                      disabled={differencesCount === 0 || selectedTool === PDFTronTools.RESYNC}
                      selectedTool={selectedTool}
                    />
                    {selectedModule === CTFModuleTypes.text && <ResyncToggle disabled={multipleSelectionMode} />}
                    {selectedModule === CTFModuleTypes.graphics && (
                      <MergeGraphicsButton disabled={differencesCount === 0} />
                    )}
                    <MarkupToggleButton />
                    <OrderingList />
                    <FilterList variant="results" disabled={false} />
                  </Grid>
                </Grid>
                {/* Internal only */}
                {isInternal && lastJobCtfVersion && (
                  <Typography className={classes.ctfVersionText}>CTF Version: {lastJobCtfVersion}</Typography>
                )}
                {isStepAndRepeat && selectedModule === CTFModuleTypes.graphics && (
                  <Grid item sx={{ marginTop: 1, maxWidth: 400 }} id="listOfRepeats">
                    <Typography variant="body2" className={classes.totalRepeatsText}>
                      Total repeats: {repeats.length}
                    </Typography>
                    <StepAndRepeatDropdown repeatsPerPage={repeatsPerPage} isStepAndRepeat={isStepAndRepeat} />
                  </Grid>
                )}
              </Grid>
              <Grid item className={classes.listOfDifferences} id="listOfDifferences">
                {useStepAndRepeatView ? (
                  <StepAndRepeatListOfDifferences
                    multipleSelectionMode={multipleSelectionMode}
                    selectedDifferenceIds={selectedDifferenceIds}
                    onDifferenceSelection={handleDifferenceSelection}
                  />
                ) : (
                  <ListOfDifferences
                    multipleSelectionMode={multipleSelectionMode}
                    selectedDifferenceIds={selectedDifferenceIds}
                    onDifferenceSelection={handleDifferenceSelection}
                  />
                )}
              </Grid>
            </>
          }
        </>
      )}
      <BulkActionModal
        open={openBulkActionModal}
        onClose={handleCloseBulkActionModal}
        onApply={handleApplyBulkAction}
        onApplyOverrides={handleBulkUpdate}
        onCancelOverrides={handleCancelOverrides}
        selectedCount={selectedDifferences.length}
        overrides={bulkOverrides}
        isApplying={isBulkApplying}
      />
    </Grid>
  );
};

/**
 * Memoized because `ResultTab` re-renders on every scroll tick to drive text match prefetching, and
 * this tree is far too big to re-render at that rate. This only works while the component stays
 * prop-less: adding a prop that isn't referentially stable (an inline callback, an object literal)
 * silently defeats the shallow compare and brings the scroll jank back with no test failure.
 */
export default React.memo(ResultTabView);
